import { HugeiconsIcon } from "@hugeicons/react";
import { Alert02Icon, ArrowLeftRightIcon, CheckmarkCircle01Icon, Copy01Icon, Database01Icon, GitBranchIcon, HardDriveIcon, LockIcon, QuestionIcon, Shield01Icon, TerminalIcon, Tick02Icon } from "@hugeicons-pro/core-bulk-rounded";
import type { IconSvgElement } from "@hugeicons/react";

import { useState } from "react";

import { PageHeader } from "@/components/layout/page-header";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FadeIn } from "@/components/ui/motion";
import { MAIL_TEXT } from "@/lib/transfer-text";

/** Section card with a hairline header — matches the Benchmarks/Bronnen pattern. */
function Section({
  title, icon: Ico, count, children,
}: { title: string; icon: IconSvgElement; count?: string; children: React.ReactNode }) {
  return (
    <Card className="overflow-hidden p-0">
      <div className="band-header flex items-center gap-2.5 px-5 py-4">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary ring-1 ring-primary/15">
          <HugeiconsIcon icon={Ico} size={18} />
        </span>
        <h3 className="font-heading text-base font-semibold">{title}</h3>
        {count && <Badge variant="outline" size="sm" className="ml-auto">{count}</Badge>}
      </div>
      {children}
    </Card>
  );
}

/** One answered point: their question, our answer. */
function Answer({ n, q, children }: { n: string; q: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-4 bg-card p-5">
      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary">
        {n}
      </span>
      <div className="min-w-0 space-y-1.5">
        <p className="font-medium">{q}</p>
        <div className="space-y-1.5 text-sm text-muted-foreground">{children}</div>
      </div>
    </div>
  );
}

function Code({ children }: { children: React.ReactNode }) {
  return (
    <pre className="overflow-x-auto rounded-lg border border-border bg-[hsl(140_14%_97%)] p-4 text-xs leading-relaxed text-foreground dark:bg-[hsl(140_8%_9%)]">
      <code>{children}</code>
    </pre>
  );
}

/** Compact inventory table that stays readable on mobile. */
function Inventory({ head, rows }: { head: [string, string, string]; rows: [string, string, string][] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[520px] text-sm">
        <thead>
          <tr className="border-b border-border">
            {head.map((h) => (
              <th key={h} className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground/70">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(([a, b, c], i) => (
            <tr key={a} className={i ? "border-t border-border/60" : ""}>
              <td className="px-5 py-3 font-medium">{a}</td>
              <td className="px-5 py-3 text-muted-foreground">{b}</td>
              <td className="px-5 py-3">{c}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const ENDPOINTS: [string, string][] = [
  ["graph.facebook.com", "Meta ads + Instagram organic"],
  ["googleads.googleapis.com", "Google Ads"],
  ["oauth2.googleapis.com", "Google token refresh"],
  ["analyticsdata.googleapis.com", "GA4 rapportage"],
  ["analyticsadmin.googleapis.com", "GA4 properties"],
  ["business-api.tiktok.com", "TikTok Ads"],
  ["reporting.api.bingads.microsoft.com", "Microsoft Advertising"],
  ["login.microsoftonline.com", "Microsoft token refresh"],
  ["ads-api.reddit.com · www.reddit.com", "Reddit Ads + token"],
  ["api.linkedin.com", "LinkedIn Ads"],
  ["a.klaviyo.com", "Klaviyo"],
  ["api.pinterest.com", "Pinterest Ads"],
  ["api.teamtailor.com", "Teamtailor (sollicitaties)"],
  ["api.resend.com", "Uitgaande e-mail"],
  ["acme-v02.api.letsencrypt.org", "TLS-certificaat"],
];

/** Clipboard with a fallback: the async API is unavailable on non-HTTPS origins
 *  and can be blocked by permissions, in which case we drop to a hidden textarea. */
async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fall through */ }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

export function Transfer() {
  const [copied, setCopied] = useState<"idle" | "ok" | "fail">("idle");

  async function copyAsMail() {
    const ok = await copyText(MAIL_TEXT);
    setCopied(ok ? "ok" : "fail");
    setTimeout(() => setCopied("idle"), 2500);
  }

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Overdracht"
        icon={ArrowLeftRightIcon}
        title="Overdracht naar de Prado-omgeving"
        description="Antwoord op de voorwaarden van Thijs, met de architectuur, het migratie-runbook en de openstaande beslissingen."
        tabs={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={copyAsMail}>
              {copied === "ok"
                ? <HugeiconsIcon icon={Tick02Icon} size={16} />
                : <HugeiconsIcon icon={Copy01Icon} size={16} />}
              {copied === "ok" ? "Gekopieerd" : copied === "fail" ? "Kopiëren mislukt" : "Kopieer als e-mail"}
            </Button>
          </div>
        }
      />

      {/* ---------------------------------------------------------------- */}
      <FadeIn>
        <Section title="Antwoord op jullie voorwaarden" icon={CheckmarkCircle01Icon} count="akkoord">
          <div>
            <div className="grid grid-cols-1 gap-px overflow-hidden rounded-t-xl bg-border">
            <Answer n="1" q="Geen SSH — Azure Bastion en Entra ID">
              <p>Akkoord. We hebben geen permanente servertoegang nodig. Voor de eenmalige migratie en voor incidenten volstaat een tijdsgebonden gastaccount met MFA.</p>
              <p>Namen en e-mailadressen bezorgen we samen met dit document.</p>
            </Answer>
            <Answer n="2" q="GitHub en GitHub Actions in plaats van de eigen Git-server">
              <p>Akkoord, en het is een verbetering. Vandaag draait er een eigen Git-server (Gitea) en wordt er vanaf een werkstation gedeployed. In jullie model verdwijnt Gitea volledig.</p>
              <p>Wij leveren de Dockerfiles, de compose-bestanden en de workflows mee in de repository. De secrets verhuizen naar GitHub Actions secrets; er blijft geen <span className="font-mono text-xs">.env</span> op de server staan.</p>
              <p>Te kiezen: GitHub Container Registry of Azure Container Registry.</p>
            </Answer>
            <Answer n="3" q="Server, capaciteit en datagroei">
              <p>4 vCPU, 8 GB RAM en 80 GB SSD is ruim voldoende.</p>
              <p><strong>Datagroei:</strong> de database is vandaag <strong>28 MB</strong>, de volledige omgeving <strong>225 MB</strong>. Er komt ongeveer <strong>5 à 10 MB per maand</strong> bij — dagelijkse rapportagecijfers, geen media en geen klantdata. 80 GB volstaat jarenlang.</p>
            </Answer>
            <Answer n="4" q="Domeinnaam">
              <p>Vandaag draait alles op <span className="font-mono text-xs">pulse.sidestream.be</span>, met <span className="font-mono text-xs">git.pulse.sidestream.be</span> voor de Git-server (die vervalt).</p>
              <p>Voorstel: <span className="font-mono text-xs">pulse.prado.com</span>. Let wel: een nieuwe hostname betekent dat élke platformkoppeling opnieuw geregistreerd en geautoriseerd moet worden — zie de inventaris.</p>
            </Answer>
            <Answer n="5" q="DNS, certificaat en WAF">
              <p>Eén A-record volstaat: <span className="font-mono text-xs">pulse.prado.com</span> → het IP van de server, TTL 300 tijdens de migratie.</p>
              <p><strong>Omdat er een WAF voor komt, kiezen we DNS-01</strong> in plaats van HTTP-01. HTTP-01 vereist dat Let's Encrypt rechtstreeks een bestand op poort 80 kan ophalen, wat een WAF doorgaans blokkeert. Daarvoor hebben we een API-token nodig om een TXT-record te zetten — of jullie termineren TLS op de WAF, dan hebben wij niets nodig.</p>
            </Answer>
            <Answer n="6" q="Firewall en uitgaande endpoints">
              <p>Inkomend 80 en 443 volstaat; poort 22 mag dicht. De volledige lijst uitgaande endpoints staat hieronder — alles is uitgaand HTTPS.</p>
            </Answer>
            <Answer n="7" q="Eigenaarschap van de platform-apps">
              <p>Terecht punt, en vandaag niet volledig in orde. De inventaris hieronder geeft per platform de eigenaar, de bewaarplaats van de secrets en wat er moet wijzigen.</p>
            </Answer>
            <Answer n="8" q="Resend">
              <p>Resend verstuurt de transactionele mails van de influencer-app (voorstellen, contracten, uitnodigingen) vanaf <span className="font-mono text-xs">marketing.prado.com</span>. Bewust een API-koppeling, geen marketingplatform.</p>
              <p>Willen jullie een bestaand platform gebruiken, dan kan dat: het verzenden zit achter één functie. Laat weten welk platform en of het SMTP of API wordt.</p>
            </Answer>
            <Answer n="9" q="Back-up">
              <p>Azure Backup op de VM plus een dagelijkse database-dump naar blob storage is precies wat nodig is. Een dump is enkele tientallen MB.</p>
            </Answer>
            <Answer n="10" q="Onderhoudsvenster en kritikaliteit">
              <p>Voorstel: een weekavond vanaf 19u, of zaterdagvoormiddag. Twee uur volstaat.</p>
              <p><strong>Niet productiekritisch.</strong> Het is een interne rapportageomgeving zonder klantverkeer; de cijfers worden één keer per nacht opgehaald. Een storing betekent verouderde rapportage — geen omzetverlies, geen dataverlies.</p>
            </Answer>
            </div>
          </div>
        </Section>
      </FadeIn>

      {/* ---------------------------------------------------------------- */}
      <FadeIn delay={0.05}>
        <Section title="Architectuur" icon={HardDriveIcon}>
          <div className="space-y-3 px-5 py-4 text-sm">
            <p className="text-muted-foreground">
              Alles draait in Docker Compose op één host. Enkel Caddy is publiek bereikbaar;
              elke andere container luistert op het interne Docker-netwerk of op localhost.
            </p>
            <Code>{`PUBLIEK   caddy               80, 443    reverse proxy + TLS
            ├── /data/       → dashboard (statisch; / stuurt door naar /data/)
            ├── /managing/   → influencer-app (statisch)
            ├── /rest /auth /storage /functions  → API-gateway
            └── /oauth/*     → callback-service (127.0.0.1:8090)

INTERN    supabase-envoy      8000  API-gateway (127.0.0.1)
          supabase-db         5432  PostgreSQL 15   ← alle data
          supabase-rest       3000  PostgREST
          supabase-auth             GoTrue (login)
          supabase-storage    5000  bestanden
          supabase-edge-functions   Deno (e-mail, invites, OAuth)
          supabase-meta / studio / imgproxy
          gitea               3000  eigen Git-server  ← vervalt

VOLUMES   volumes/db          PostgreSQL data  ← het enige dat telt
          volumes/storage     geüploade bestanden
          volumes/functions   code van de edge functions

CRON      11 koppelingen, dagelijks tussen 05u30 en 08u30`}</Code>
          </div>
        </Section>
      </FadeIn>

      {/* ---------------------------------------------------------------- */}
      <FadeIn delay={0.1}>
        <Section title="Migratie-runbook (GitHub Actions, zonder SSH)" icon={GitBranchIcon}>
          <div className="space-y-3 px-5 py-4 text-sm">
            <ol className="ml-4 list-decimal space-y-2 text-muted-foreground marker:text-muted-foreground/60">
              <li>Repositories naar jullie GitHub-organisatie. Wij voegen Dockerfiles, compose-bestanden en workflows toe. Gitea vervalt.</li>
              <li>Secrets invoeren als GitHub Actions secrets. Er komt geen <span className="font-mono text-xs">.env</span> op de server.</li>
              <li>De pipeline bouwt de images en pusht ze naar de registry.</li>
              <li>De self-hosted runner haalt de images op en start de compose-stack.</li>
              <li><strong>Eenmalig:</strong> database-dump van de huidige omgeving inladen in de nieuwe. Dit is de enige datamigratie en gebeurt via de runner, niet via SSH.</li>
              <li>Koppelingen hertesten, daarna DNS omzetten (TTL 300) en het certificaat via DNS-01 laten uitgeven.</li>
              <li>Bij een nieuwe hostname: elke platformkoppeling opnieuw registreren en autoriseren.</li>
            </ol>
            <div className="rounded-lg border border-border bg-[hsl(140_14%_97%)] p-4 dark:bg-[hsl(140_8%_9%)]">
              <p className="text-sm font-medium">Downtime en terugval</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Verwachte downtime: <strong>1 à 2 uur</strong>, enkel voor het dashboard. De oude omgeving
                blijft ondertussen draaien. Terugval = het A-record terugzetten; met TTL 300 is dat binnen
                enkele minuten actief. Pas na een geslaagde controle zetten we de oude omgeving stop.
              </p>
            </div>
          </div>
        </Section>
      </FadeIn>

      {/* ---------------------------------------------------------------- */}
      <FadeIn delay={0.15}>
        <Section title="Uitgaande endpoints" icon={LockIcon} count={`${ENDPOINTS.length} hosts`}>
          <div className="px-5 py-4">
            <p className="mb-3 text-sm text-muted-foreground">
              Alles is uitgaand HTTPS (poort 443). Deze lijst kan gebruikt worden om de uitgaande
              regels gericht te beperken.
            </p>
            <div className="grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-border bg-border sm:grid-cols-2">
              {ENDPOINTS.map(([host, why]) => (
                <div key={host} className="bg-card px-4 py-2.5">
                  <p className="font-mono text-xs">{host}</p>
                  <p className="text-xs text-muted-foreground">{why}</p>
                </div>
              ))}
            </div>
          </div>
        </Section>
      </FadeIn>

      {/* ---------------------------------------------------------------- */}
      <FadeIn delay={0.2}>
        <Section title="Inventaris platform-apps" icon={Shield01Icon} count="actie vereist">
          <Inventory
            head={["Platform", "Eigenaar vandaag", "Actie"]}
            rows={[
              ["Meta + Instagram", "Prado Business (systeemgebruiker)", "Geen"],
              ["Google Ads + GA4", "Persoonlijk Google-account", "Overzetten naar Prado-account"],
              ["Microsoft / Bing", "Prado werk-account", "Geen"],
              ["TikTok Ads", "Prado advertiser", "Geen"],
              ["Reddit Ads", "Persoonlijk Reddit-account", "Overzetten naar Prado-account"],
              ["LinkedIn Ads", "Persoonlijk account", "Overzetten naar Prado-account"],
              ["Pinterest Ads", "Prado app (Trial) — cijfers lopen tijdelijk via Converge", "Standard access aanvragen"],
              ["Klaviyo", "Prado account", "Geen"],
              ["Teamtailor", "Prado account", "Geen"],
              ["Resend (e-mail)", "Persoonlijk account", "Overzetten naar Prado-account"],
            ]}
          />
          <div className="border-t border-border px-5 py-4">
            <p className="text-sm font-medium">Waar de secrets vandaag staan</p>
            <p className="mt-1 text-sm text-muted-foreground">
              De tokens staan in de database (tabel <span className="font-mono text-xs">platform_connections</span>),
              enkel bereikbaar met de service-sleutel en niet vanuit de browser. Daarnaast staan er
              omgevingsvariabelen in <span className="font-mono text-xs">.env</span> op de server (rechten 600) en in de
              edge-function secrets. Die twee laatste verhuizen naar GitHub Actions secrets.
            </p>
          </div>
        </Section>
      </FadeIn>

      {/* ---------------------------------------------------------------- */}
      <FadeIn delay={0.25}>
        <Section title="Openstaande beslissingen" icon={QuestionIcon} count="5">
          <div>
            <div className="grid grid-cols-1 gap-px overflow-hidden rounded-t-xl bg-border sm:grid-cols-2">
            {[
              { t: "Hostname", d: "pulse.prado.com of een andere. Bepaalt of alle koppelingen opnieuw geautoriseerd moeten worden." },
              { t: "Certificaat", d: "DNS-01 met een API-token, of TLS termineren op de WAF." },
              { t: "Registry", d: "GitHub Container Registry of Azure Container Registry." },
              { t: "E-mail", d: "Resend behouden of overschakelen naar een bestaand Prado-platform." },
              { t: "Eigenaarschap apps", d: "Wie wordt de Prado-eigenaar van de Google- en Reddit-koppeling." },
            ].map((x) => (
              <div key={x.t} className="bg-card p-5">
                <div className="flex items-center gap-2">
                  <HugeiconsIcon icon={Alert02Icon} size={15} />
                  <p className="text-sm font-medium">{x.t}</p>
                </div>
                <p className="mt-1.5 text-sm text-muted-foreground">{x.d}</p>
              </div>
            ))}
            </div>
          </div>
        </Section>
      </FadeIn>

      {/* ---------------------------------------------------------------- */}
      <FadeIn delay={0.3}>
        <Section title="Wat we van jullie nodig hebben" icon={TerminalIcon}>
          <div>
          <div className="grid grid-cols-1 gap-px overflow-hidden rounded-t-xl bg-border sm:grid-cols-2">
            {[
              "Gastaccounts (Entra ID) voor de personen die we doorgeven",
              "De GitHub-organisatie en de keuze van registry",
              "Bevestiging van de hostname",
              "API-token voor DNS-01, of TLS op de WAF",
              "Bevestiging van het e-mailplatform",
              "Twee mogelijke onderhoudsmomenten",
            ].map((t) => (
              <div key={t} className="flex items-start gap-2.5 bg-card p-4">
                <HugeiconsIcon icon={Database01Icon} size={15} />
                <span className="text-sm">{t}</span>
              </div>
            ))}
            </div>
          </div>
        </Section>
      </FadeIn>

      <p className="px-1 pb-2 text-xs text-muted-foreground/70">
        Een call om dit door te nemen is prima — dit document dient dan als agenda.
      </p>
    </div>
  );
}
